import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Color, ColorPickerControl } from '@iplab/ngx-color-picker';
import { FormFieldInputBaseComponent } from '../form-field-input-base.component';
import { FormFieldInputContainerComponent } from '../../container/form-field-container.component';
import { MatMenu } from '@angular/material/menu';

export type DominantColourType = {
    Color: string,
    Count: number
}

@Component({
    selector: 'lib-input-color-picker',
    templateUrl: './color-picker.component.html',
    styleUrls: ['./color-picker.component.scss'],
})
export class ColorPickerComponent extends FormFieldInputBaseComponent<string> implements OnInit, AfterViewInit {
    @ViewChild('logo', {static: true}) logo!: ElementRef<HTMLImageElement>;


    @Input() defaultValue: string | null = null;
    @Input() autoSetFormControl: boolean = false;

    //This Input is used to reset the form to form control value instead of the default value
    @Input() resetToInitialValue: boolean = false;

    @Input() set base64Image(value: string) {
        this._base64Image = value;
    }

    @Input() set imageUrl(value: string | null) {
        if (value) {
            this.GetColorsFromImageUrl(value);
        }
    }

    @Output() selectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() closed: EventEmitter<void> = new EventEmitter<void>();

    compactControl = new ColorPickerControl();
    ignoreChanges: boolean = false;

    override readonly hasSuffixIconButton: boolean = true;

    private _selectedColor: string | null = null;
    private _MouseUp = this.MouseUp.bind(this);
    private _TouchEnd = this.TouchEnd.bind(this);
    private _base64Image: string = "";
    private _firstLoad: boolean = true;

    ngAfterViewInit(): void {
        if (this.formControl.value) {
            this.compactControl.setValueFrom(this.formControl.value);
        } else {
            this.compactControl.hidePresets();
            this.ResetToDefaultColor();
        }
    }

    FieldClicked(event: any) {
        if (this.isDisabled) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    ColorChangeEvent(color: string): void {
        this._selectedColor = Color.from(color).toHexString();
        if (!this.ignoreChanges && this._selectedColor.toLowerCase() != this.formControl.value.toLowerCase()) {
            this.formControl.setValue(this._selectedColor!);
            this.formControl.isDirty = true;
            this.selectionChanged.emit();
        }
    }

    SelectionChanged(): void {
        if (this._selectedColor?.toLowerCase() !== this.formControl.value.toLowerCase()) {
            this.formControl.patchValue(this._selectedColor!);
            this.formControl.isDirty = true;
            this.selectionChanged.emit();
        }
    }

    override writeValue(value: string) {
        this.compactControl.setValueFrom(value);
    }

    MouseDown(): void {
        this.ignoreChanges = true;
        document.addEventListener('mouseup', this._MouseUp);
    }

    TouchStart(): void {
        this.ignoreChanges = true;
        document.addEventListener('touchend', this._TouchEnd);
    }

    MouseUp(): void {
        this.ignoreChanges = false;
        this.SelectionChanged();
        document.removeEventListener('mouseup', this._MouseUp);
    }

    TouchEnd(): void {
        this.ignoreChanges = false;
        this.SelectionChanged();
        document.removeEventListener('touchend', this._TouchEnd);
    }

    ResetToDefaultColor(): void {
        if (this.defaultValue) {
            this.compactControl.setValueFrom(this.defaultValue);
        } else {
            throw Error('No default value provided');
        }
    }

    OpenColorPaletteMenu(): void {
        this.container.matMenuTrigger.openMenu();
    }

    GetColorsFromImageUrl(logoUrl: string): void {
        this.ImageUrlToBase64(logoUrl)
            .then(base64 => {
                this._base64Image = base64;
                this.GetDominantColors();
            })
            .catch(error => {

            });
    }

    ColorPickerClosed(): void {
        this.closed.emit();
    }

    // Original Code by Griffin
    GetDominantColors(): DominantColourType[] {
        const dominantColors: string[] = [];

        const image = this.logo.nativeElement;
        let canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        let context = canvas.getContext('2d')!;
        context.scale(80 / image.width, 80 / image.height);
        context.drawImage(image, 0, 0);
        let imageData = context.getImageData(0, 0, 80, 80);
        let colors: DominantColourType[] = [];
        let colorFound: boolean = false;

        let pixelColor: string;

        // increment by 4 for each pixel
        for (let i = 0; i < imageData.data.length; i += 4) {
            colorFound = false;
            pixelColor = 'rgba(' + String(imageData.data[i]) + ',' + String(imageData.data[i + 1]) + ',' + String(imageData.data[i + 2]) + ',' + '1)';
            for (let currentColor of colors) {
                if (pixelColor === currentColor.Color) {
                    currentColor.Count++;
                    colorFound = true;
                    break;
                }
            }
            if (!colorFound) {
                let luma = this.CalculateLuma(pixelColor);
                if (luma < 215 && luma > 40) {
                    colors.push({Color: pixelColor, Count: 0 });
                }
            }
        }

        let mostCommonColors = colors.sort((a, b) => b.Count - a.Count).slice(0, 9);
        mostCommonColors.forEach(color => {
            dominantColors.push(this.FindHex(color.Color));
        });

        // Setting Presets if there are dominant colours
        this.compactControl.hidePresets();
        if (mostCommonColors.length) {
            this.compactControl.setColorPresets(dominantColors);
            this.compactControl.showPresets();
        } else if (!this._firstLoad && !this.resetToInitialValue) {
            this.ResetToDefaultColor();
        } else if (!this._firstLoad && this.resetToInitialValue) {
            this.compactControl.setValueFrom(this.formControl.value);
        }

        // Only set the formControl if the input is true and it's not the first load
        if (this.autoSetFormControl && !this._firstLoad) {
            this.SetDominantColourFromLogo(mostCommonColors);
        }

        this._firstLoad = false;

        return mostCommonColors;
    }

    private SetDominantColourFromLogo(colors: DominantColourType[]): void {
        colors.forEach(color => {
            if (!this.IsGrayColor(color.Color)) {
                const hex = this.FindHex(color.Color);
                this.formControl.setValue(hex);
                this.compactControl.setValueFrom(hex);
                this._selectedColor = hex;
                this.formControl.isDirty = true;
                return;
            }
        });
    }

    private CalculateLuma(rgb: string): number {
        const rgbArray = this.RGBStringToArray(rgb);
        return 0.2126*rgbArray[0] + 0.7152*rgbArray[1] + 0.0722*rgbArray[2];
    }

    private RGBStringToArray(rgbString: string): number[] {
        let rgbArray = [];
        if (rgbString) {
            // Ensure the string is in the expected format
            const matches = rgbString.match(/\(([^)]+)\)/);
            if (matches) {
                const rgbaValues = matches[1].split(',');
                rgbArray.push(+rgbaValues[0].trim());
                rgbArray.push(+rgbaValues[1].trim());
                rgbArray.push(+rgbaValues[2].trim());
            }
        }
        return rgbArray;
    }

    private FindHex(event: string): string {
        const rgbArray = this.RGBStringToArray(event);
        return "#" + this.ValueToHex(rgbArray[0]) + this.ValueToHex(rgbArray[1]) + this.ValueToHex(rgbArray[2]).toUpperCase();
    }

    private ValueToHex(value: number): string {
        let hex = value.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
    }

    private IsGrayColor(rgb: string): boolean {
        let rgbArray = this.RGBStringToArray(rgb);
        return Math.abs(rgbArray[0] - rgbArray[1]) < 10 && Math.abs(rgbArray[1] - rgbArray[2]) < 10;
    }

    private async ImageUrlToBase64(url: string): Promise<string> {
        // Fetch the image
        const response = await fetch(url);
        // Convert the response to a Blob
        const blob = await response.blob();
        // Convert the Blob to a Base64 string
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    get base64Image(): string {
        return this._base64Image;
    }
}
